import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const CompanyValuesSection = () => {
  const { language } = useLanguage();
  const [hoveredValue, setHoveredValue] = useState(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.querySelector('.values-section');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const companyValues = [
    {
      id: 'excellence',
      title: {
        fr: 'Excellence',
        ar: 'التميز',
        en: 'Excellence'
      },
      description: {
        fr: 'Nous nous engageons à fournir des services et des solutions d\'une qualité exceptionnelle, dépassant systématiquement les attentes de nos clients et établissant de nouvelles normes dans notre industrie.',
        ar: 'نحن ملتزمون بتقديم خدمات وحلول ذات جودة استثنائية، متجاوزين باستمرار توقعات عملائنا ووضع معايير جديدة في صناعتنا.',
        en: 'We are committed to providing services and solutions of exceptional quality, consistently exceeding our clients\' expectations and setting new standards in our industry.'
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      color: '#E53E3E'
    },
    {
      id: 'integrity',
      title: {
        fr: 'Intégrité',
        ar: 'النزاهة',
        en: 'Integrity'
      },
      description: {
        fr: 'Nous agissons avec honnêteté, transparence et responsabilité dans toutes nos interactions, en respectant les normes éthiques les plus élevées et en établissant des relations de confiance durables avec nos clients, partenaires et employés.',
        ar: 'نعمل بصدق وشفافية ومسؤولية في جميع تعاملاتنا، مع احترام أعلى المعايير الأخلاقية وبناء علاقات ثقة دائمة مع عملائنا وشركائنا وموظفينا.',
        en: 'We act with honesty, transparency, and accountability in all our interactions, upholding the highest ethical standards and building lasting relationships of trust with our clients, partners, and employees.'
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: '#3182CE'
    },
    {
      id: 'innovation',
      title: {
        fr: 'Innovation',
        ar: 'الابتكار',
        en: 'Innovation'
      },
      description: {
        fr: 'Nous recherchons continuellement des moyens d\'améliorer nos services, d\'adopter les technologies émergentes et de développer des solutions créatives qui anticipent les besoins en constante évolution de nos clients et de l\'industrie.',
        ar: 'نبحث باستمرار عن طرق لتحسين خدماتنا، وتبني التقنيات الناشئة، وتطوير حلول إبداعية تتوقع الاحتياجات المتطورة لعملائنا والصناعة.',
        en: 'We continually seek ways to improve our services, embrace emerging technologies, and develop creative solutions that anticipate the evolving needs of our clients and the industry.'
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: '#805AD5'
    },
    {
      id: 'collaboration',
      title: {
        fr: 'Collaboration',
        ar: 'التعاون',
        en: 'Collaboration'
      },
      description: {
        fr: 'Nous croyons en la puissance du travail d\'équipe et de la collaboration avec nos clients et partenaires pour créer des solutions optimales. Notre approche collaborative favorise le partage des connaissances, la synergie et des résultats supérieurs.',
        ar: 'نؤمن بقوة العمل الجماعي والتعاون مع عملائنا وشركائنا لإنشاء حلول مثالية. يعزز نهجنا التعاوني تبادل المعرفة والتآزر والنتائج المتميزة.',
        en: 'We believe in the power of teamwork and collaboration with our clients and partners to create optimal solutions. Our collaborative approach fosters knowledge-sharing, synergy, and superior outcomes.'
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      color: '#38A169'
    },
    {
      id: 'expertise',
      title: {
        fr: 'Expertise',
        ar: 'الخبرة',
        en: 'Expertise'
      },
      description: {
        fr: 'Nous possédons une connaissance approfondie et une expérience spécialisée dans l\'instrumentation pour le secteur Pétrole, Gaz et Industrie, et nous investissons continuellement dans le développement de nos compétences et de notre savoir-faire technique pour rester à la pointe de notre domaine.',
        ar: 'نمتلك معرفة عميقة وخبرة متخصصة في مجال القياس لقطاع النفط والغاز والصناعة، ونستثمر باستمرار في تطوير مهاراتنا وخبراتنا التقنية للبقاء في طليعة مجالنا.',
        en: 'We possess deep knowledge and specialized experience in Oil, Gas and Industry instrumentation, and we continuously invest in developing our skills and technical know-how to remain at the forefront of our field.'
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M12 14l9-5-9-5-9 5 9 5z" />
          <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5z" />
        </svg>
      ),
      color: '#DD6B20'
    },
    {
      id: 'safety',
      title: {
        fr: 'Sécurité',
        ar: 'السلامة',
        en: 'Safety'
      },
      description: {
        fr: 'Nous donnons la priorité absolue à la sécurité de nos employés, clients et partenaires. Notre certification témoigne de notre engagement envers les normes de sécurité les plus strictes dans toutes nos opérations et installations.',
        ar: 'نعطي الأولوية القصوى لسلامة موظفينا وعملائنا وشركائنا. تشهد شهادتنا على التزامنا بأعلى معايير السلامة في جميع عملياتنا ومنشآتنا.',
        en: 'We give absolute priority to the safety of our employees, clients, and partners. Our certification testifies to our commitment to the strictest safety standards in all our operations and facilities.'
      },
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      color: '#D69E2E'
    }
  ];

  const getLanguageText = (textObj) => {
    return textObj[language] || textObj.fr || textObj.en;
  };

  return (
    <section className="values-section section section-dark" style={{ 
      position: 'relative',
      padding: '6rem 0',
      overflow: 'hidden'
    }}>
      {/* Decorative elements */}
      <div style={{
        position: 'absolute',
        top: '5%',
        right: '0',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
        zIndex: 1
      }}></div>
      
      <div style={{
        position: 'absolute',
        bottom: '5%',
        left: '0',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
        zIndex: 1
      }}></div>
      
      <div className="container" style={{ 
        position: 'relative', 
        zIndex: 2,
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 1.5rem'
      }}>
        {/* Section header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '4rem'
        }}>
          <span style={{
            display: 'inline-block',
            padding: '0.5rem 1.5rem',
            backgroundColor: 'rgba(255, 107, 0, 0.1)',
            color: 'var(--assco-orange)',
            borderRadius: '30px',
            fontWeight: '600',
            fontSize: '0.875rem',
            marginBottom: '1rem',
            textTransform: 'uppercase'
          }}>
            {language === 'fr' ? 'NOS VALEURS' : 
             language === 'ar' ? 'قيمنا' : 
             'OUR VALUES'}
          </span>

          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '1.5rem',
            color: 'white',
            position: 'relative',
            display: 'inline-block'
          }}>
            {language === 'fr' ? 'Les valeurs qui nous animent' : 
             language === 'ar' ? 'القيم التي تحركنا' : 
             'The Values That Drive Us'}
            <span style={{
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: 'var(--assco-orange)',
              borderRadius: '2px'
            }}></span>
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: '700px',
            margin: '0 auto',
            lineHeight: '1.7'
          }}>
            {language === 'fr' 
              ? 'Chez ASSCO, nos valeurs reflètent notre engagement envers l\'excellence, l\'intégrité et l\'innovation dans le secteur Pétrole, Gaz et Industrie. Ces principes guident nos actions quotidiennes et notre vision à long terme.' 
              : language === 'ar'
              ? 'في ASSCO، تعكس قيمنا التزامنا بالتميز والنزاهة والابتكار في قطاع النفط والغاز والصناعة. توجه هذه المبادئ أعمالنا اليومية ورؤيتنا طويلة المدى.'
              : 'At ASSCO, our values reflect our commitment to excellence, integrity, and innovation in the Oil, Gas and Industry sector. These principles guide our daily actions and our long-term vision.'}
          </p>
        </div>
        
        {/* Values cards grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '2rem',
          marginBottom: '3rem',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          {companyValues.map((value, index) => (
            <div 
              key={value.id}
              className={`value-card fade-in ${isIntersecting ? 'active' : ''}`}
              style={{
                position: 'relative',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '24px',
                padding: '2.5rem 2rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                transition: 'all 0.4s ease',
                cursor: 'pointer',
                animationDelay: `${0.1 * index}s`,
                minHeight: '320px',
                boxShadow: hoveredValue === value.id 
                  ? `0 25px 50px -12px ${value.color}30, 0 0 0 2px ${value.color}30`
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                transform: `${isIntersecting ? 'scale(1)' : 'scale(0.9)'} ${hoveredValue === value.id ? 'translateY(-15px) scale(1.02)' : 'translateY(0)'}`,
                opacity: isIntersecting ? 1 : 0,
                border: hoveredValue === value.id ? `2px solid ${value.color}` : '2px solid transparent',
                overflow: 'hidden'
              }}
              onMouseEnter={() => setHoveredValue(value.id)}
              onMouseLeave={() => setHoveredValue(null)}
            >
              {/* Background gradient overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: hoveredValue === value.id 
                  ? `linear-gradient(135deg, ${value.color}15 0%, ${value.color}05 100%)`
                  : 'transparent',
                transition: 'all 0.4s ease',
                zIndex: 1
              }}></div>

              {/* Background pattern */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${value.color.replace('#', '%23')}' fill-opacity='0.04'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                opacity: hoveredValue === value.id ? 1 : 0.3,
                transition: 'opacity 0.4s ease',
                zIndex: 1
              }}></div>
              
              {/* Content */}
              <div style={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%'
              }}>
                {/* Icon container */}
                <div style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: hoveredValue === value.id ? value.color : `${value.color}15`,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem',
                  color: hoveredValue === value.id ? 'white' : value.color,
                  transition: 'all 0.4s ease',
                  boxShadow: hoveredValue === value.id 
                    ? `0 10px 20px ${value.color}40`
                    : `0 5px 15px ${value.color}20`,
                  transform: hoveredValue === value.id ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)'
                }}>
                  <div style={{ width: '40px', height: '40px' }}>
                    {value.icon}
                  </div>
                </div>
                
                {/* Title */}
                <h3 style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: hoveredValue === value.id ? value.color : '#1a202c',
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}>
                  {getLanguageText(value.title)}
                  <div style={{
                    position: 'absolute',
                    bottom: '-8px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: hoveredValue === value.id ? '40px' : '0px',
                    height: '3px',
                    backgroundColor: value.color,
                    borderRadius: '2px',
                    transition: 'all 0.3s ease'
                  }}></div>
                </h3>
                
                {/* Description */}
                <p style={{
                  fontSize: '1rem',
                  lineHeight: '1.6',
                  color: '#4a5568',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  fontWeight: '400'
                }}>
                  {/* Show shorter description for better readability */}
                  {getLanguageText(value.title) === 'Excellence' && language === 'fr' ? 'Nous nous engageons à fournir des services de la plus haute qualité, dépassant les attentes de nos clients.' :
                   getLanguageText(value.title) === 'Excellence' && language === 'ar' ? 'نحن ملتزمون بتقديم خدمات بأعلى جودة، متجاوزين توقعات عملائنا.' :
                   getLanguageText(value.title) === 'Excellence' ? 'We are committed to providing the highest quality services, exceeding our clients\' expectations.' :
                   getLanguageText(value.title) === 'Intégrité' && language === 'fr' ? 'Nous agissons avec honnêteté, transparence et responsabilité dans toutes nos interactions.' :
                   getLanguageText(value.title) === 'Intégrité' && language === 'ar' ? 'نعمل بصدق وشفافية ومسؤولية في جميع تعاملاتنا.' :
                   getLanguageText(value.title) === 'Integrity' ? 'We act with honesty, transparency, and accountability in all our interactions.' :
                   getLanguageText(value.title) === 'Innovation' && language === 'fr' ? 'Nous recherchons continuellement des moyens d\'améliorer nos services et d\'adopter les nouvelles technologies.' :
                   getLanguageText(value.title) === 'Innovation' && language === 'ar' ? 'نبحث باستمرار عن طرق لتحسين خدماتنا وتبني التقنيات الجديدة.' :
                   getLanguageText(value.title) === 'Innovation' ? 'We continually seek ways to improve our services and embrace new technologies.' :
                   getLanguageText(value.title) === 'Collaboration' && language === 'fr' ? 'Nous croyons en la puissance du travail d\'équipe avec nos clients et partenaires.' :
                   getLanguageText(value.title) === 'Collaboration' && language === 'ar' ? 'نؤمن بقوة العمل الجماعي مع عملائنا وشركائنا.' :
                   getLanguageText(value.title) === 'Collaboration' ? 'We believe in the power of teamwork with our clients and partners.' :
                   getLanguageText(value.title) === 'Expertise' && language === 'fr' ? 'Nous possédons une connaissance approfondie dans l\'instrumentation pour le secteur Pétrole, Gaz et Industrie.' :
                   getLanguageText(value.title) === 'Expertise' && language === 'ar' ? 'نمتلك معرفة عميقة في مجال القياس لقطاع النفط والغاز والصناعة.' :
                   getLanguageText(value.title) === 'Expertise' ? 'We possess deep knowledge in Oil, Gas and Industry instrumentation.' :
                   getLanguageText(value.title) === 'Sécurité' && language === 'fr'                       ? 'Nous donnons la priorité absolue à la sécurité avec notre certification.' :
                      getLanguageText(value.title) === 'Sécurité' && language === 'ar' ? 'نعطي الأولوية القصوى للسلامة مع شهادتنا.' :
                      'We give absolute priority to safety with our certification.'}
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Value highlight box */}
        <div style={{
          marginTop: '5rem',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '24px',
          padding: '3rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.15)'
        }}>
          {/* Background pattern */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23FF6B00' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            opacity: 0.5,
            zIndex: 1
          }}></div>
          
          <div style={{
            position: 'relative',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'rgba(255, 107, 0, 0.1)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1.5rem'
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="var(--assco-orange)">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            
            <h3 style={{
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '1rem',
              color: 'white'
            }}>
              {language === 'fr' ? 'Notre engagement envers l\'excellence' : 
               language === 'ar' ? 'التزامنا بالتميز' : 
               'Our Commitment to Excellence'}
            </h3>
            
            <div style={{
              width: '60px',
              height: '4px',
              backgroundColor: 'var(--assco-orange)',
              borderRadius: '2px',
              marginBottom: '2rem'
            }}></div>
            
            <p style={{
              fontSize: '1.25rem',
              lineHeight: '1.8',
              color: 'rgba(255, 255, 255, 0.8)',
              maxWidth: '800px',
              marginBottom: '2.5rem'
            }}>
              {language === 'fr' 
                ? 'Chez ASSCO, nos valeurs ne sont pas simplement des mots sur une page—elles sont le fondement de tout ce que nous faisons. Notre équipe s\'engage à intégrer ces principes dans chaque interaction, chaque service et chaque solution que nous offrons.' 
                : language === 'ar'
                ? 'في ASSCO، قيمنا ليست مجرد كلمات على صفحة - إنها أساس كل ما نفعله. يلتزم فريقنا بدمج هذه المبادئ في كل تفاعل وكل خدمة وكل حل نقدمه.'
                : 'At ASSCO, our values are not just words on a page—they are the foundation of everything we do. Our team is committed to embedding these principles in every interaction, service, and solution we provide.'}
            </p>
            
            <div style={{
              display: 'flex',
              gap: '1rem'
            }}>
              <button
                style={{
                  backgroundColor: 'var(--assco-orange)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '100px',
                  padding: '0.875rem 2rem',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 10px 15px -3px rgba(255, 107, 0, 0.2), 0 4px 6px -2px rgba(255, 107, 0, 0.05)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(255, 107, 0, 0.2), 0 10px 10px -5px rgba(255, 107, 0, 0.1)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(255, 107, 0, 0.2), 0 4px 6px -2px rgba(255, 107, 0, 0.05)';
                }}
              >
                {language === 'fr' ? 'Découvrir nos services' : 
                 language === 'ar' ? 'اكتشف خدماتنا' : 
                 'Discover Our Services'}
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
              
              <button
                style={{
                  backgroundColor: 'transparent',
                  color: '#1a202c',
                  border: '2px solid #e2e8f0',
                  borderRadius: '100px',
                  padding: '0.875rem 2rem',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7fafc';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {language === 'fr' ? 'Contactez-nous' : 
                 language === 'ar' ? 'اتصل بنا' : 
                 'Contact Us'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .fade-in.active {
          animation: fadeIn 0.6s ease forwards;
        }

        @media (max-width: 900px) {
          div[style*="grid-template-columns: repeat(3, 1fr)"] {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1.5rem !important;
          }
          
          .value-card {
            min-height: 280px !important;
            padding: 2rem 1.5rem !important;
          }
        }
        
        @media (max-width: 640px) {
          div[style*="grid-template-columns: repeat(3, 1fr)"] {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
            max-width: 400px !important;
          }
          
          .value-card {
            min-height: 260px !important;
            padding: 2rem 1.5rem !important;
          }
          
          h3[style*="font-size: 1.75rem"] {
            font-size: 1.5rem !important;
          }
          
          div[style*="width: 80px"] {
            width: 70px !important;
            height: 70px !important;
          }
          
          div[style*="width: 40px"] {
            width: 35px !important;
            height: 35px !important;
          }
        }
        
        @media (max-width: 480px) {
          .container {
            padding: 0 1rem !important;
          }
          
          h2[style*="font-size: 2.5rem"] {
            font-size: 2rem !important;
          }
          
          p[style*="font-size: 1.125rem"] {
            font-size: 1rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default CompanyValuesSection;

